// ============================================
// HYBRID DEALS / COLLAB LIST
// Ported from Influencer.in-Brandconnect HybridDeals.tsx
// ============================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPlannerCampaigns, getPlannerCampaignDeals } from '../PlannerCampaign/api/plannerCampaignApi';

// NOTE FOR BACKEND: The Collab List page needs a global "list all deals" endpoint:
//   GET /api/v1/campaigns/creators  (across all campaigns for the authenticated user)
// Until that exists, this page fetches all campaigns then their deals — which is
// inefficient. Please add the endpoint described in MISSING.md #3.

// ---- Sample Data (fallback only) ----

const STATUS_STYLES = {
    ACTIVE: { bg: '#d1fae5', color: '#065f46' },
    COMPLETED: { bg: '#dbeafe', color: '#1d4ed8' },
    CANCELED: { bg: '#f3f4f6', color: '#6b7280' },
};

const CONSENT_STYLES = {
    linked: { label: '✓ Linked', bg: '#d1fae5', color: '#065f46' },
    pending: { label: '⏳ Pending', bg: '#fef3c7', color: '#92400e' },
    declined: { label: '✗ Declined', bg: '#fee2e2', color: '#991b1b' },
};

// ---- Deal Card ----
function DealCard({ deal, onComplete, onCancel, onSendReminder }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const ss = STATUS_STYLES[deal.status] || STATUS_STYLES.CANCELED;
    const cs = CONSENT_STYLES[deal.consentStatus] || CONSENT_STYLES.pending;
    const progress = deal.expectedVideos > 0 ? (deal.linkedVideos / deal.expectedVideos) * 100 : 0;

    return (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #f3f4f6', padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'}>
            {/* Top row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#fef3c7,#fde68a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#92400e', fontSize: 18, flexShrink: 0 }}>
                        {deal.creatorName[0]}
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{deal.creatorName}</span>
                            {deal.bcEnabled && <span style={{ padding: '1px 5px', background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 3 }}>YCP</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{deal.creatorChannelId}</div>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ padding: '3px 10px', background: ss.bg, color: ss.color, fontSize: 11, fontWeight: 700, borderRadius: 20 }}>{deal.status}</span>
                    <div style={{ position: 'relative' }}>
                        <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: 18, color: '#9ca3af' }}>⋮</button>
                        {menuOpen && (
                            <div style={{ position: 'absolute', right: 0, top: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4, zIndex: 10, minWidth: 150 }} onClick={e => e.stopPropagation()}>
                                {deal.status === 'ACTIVE' && <>
                                    <button style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#374151', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => { onComplete(); setMenuOpen(false); }}>✓ Mark Complete</button>
                                    <button style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 13, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => { onCancel(); setMenuOpen(false); }}>✗ Cancel Deal</button>
                                </>}
                                <Link to={`/brandconnect/campaign/${deal.campaignId}/dashboard`} style={{ display: 'block', padding: '8px 12px', fontSize: 13, color: '#374151', textDecoration: 'none' }} onClick={() => setMenuOpen(false)}>View Campaign</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Campaign link */}
            <Link to={`/brandconnect/campaign/${deal.campaignId}/dashboard`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#b45309', textDecoration: 'none', marginBottom: 14 }}>
                Campaign: {deal.campaignName}
                {deal.campaignCode && (
                    <span style={{ padding: '1px 8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, color: '#dc2626', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>{deal.campaignCode}</span>
                )}
            </Link>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
                {[['Expected', deal.expectedVideos, '#111827'], ['Linked', deal.linkedVideos, '#16a34a'], ['Cost', deal.cost, '#111827']].map(([l, v, c]) => (
                    <div key={l} style={{ textAlign: 'center', padding: '10px 8px', background: '#f9fafb', borderRadius: 10 }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: c }}>{v}</div>
                        <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', marginTop: 2 }}>{l}</div>
                    </div>
                ))}
            </div>

            {/* Progress Bar */}
            <div style={{ marginBottom: deal.bcEnabled ? 14 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>
                    <span>Video Progress</span><span>{deal.linkedVideos}/{deal.expectedVideos}</span>
                </div>
                <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: '#22c55e', borderRadius: 3, transition: 'width 0.4s' }} />
                </div>
            </div>

            {/* BC Consent */}
            {deal.bcEnabled && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>Consent:</span>
                        <span style={{ padding: '2px 10px', background: cs.bg, color: cs.color, fontSize: 11, fontWeight: 700, borderRadius: 20 }}>{cs.label}</span>
                    </div>
                    {deal.consentStatus === 'pending' && (
                        <button onClick={onSendReminder} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Send Reminder</button>
                    )}
                </div>
            )}
        </div>
    );
}

// ---- Main Component ----
export default function HybridDeals() {
    const [statusFilter, setStatusFilter] = useState('all');
    const [bcOnlyFilter, setBcOnlyFilter] = useState(false);
    const [allDeals, setAllDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchDeals = () => {
        setLoading(true);
        setError(null);
        // Fetch all campaigns, then aggregate their deals.
        // TODO: Replace with a single GET /api/v1/campaigns/creators endpoint when available.
        getPlannerCampaigns()
            .then(async ({ campaigns }) => {
                const dealArrays = await Promise.all(
                    campaigns.map(c =>
                        getPlannerCampaignDeals(c.id)
                            .then(r => (r.deals || []).map(d => ({
                                ...d,
                                campaignId: c.id,
                                campaignName: c.name,
                                campaignCode: c.bcCampaignCode || null,
                                bcEnabled: c.bcEnabled,
                            })))
                            .catch(() => [])
                    )
                );
                setAllDeals(dealArrays.flat());
            })
            .catch(() => setError('Failed to load deals. Please try again.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchDeals(); }, []);

    const filteredDeals = allDeals.filter(deal => {
        if (statusFilter !== 'all' && deal.status !== statusFilter.toUpperCase()) return false;
        if (bcOnlyFilter && !deal.bcEnabled) return false;
        return true;
    });

    const totalDeals = allDeals.length;
    const activeDeals = allDeals.filter(d => d.status === 'ACTIVE').length;
    const bcDeals = allDeals.filter(d => d.bcEnabled).length;
    const pendingConsent = allDeals.filter(d => d.bcEnabled && d.bcConsentStatus === 'CONSENT_STATE_PENDING').length;

    const sel = { padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, color: '#374151', background: '#fff', outline: 'none' };

    return (
        <div style={{ padding: 24, background: '#f9fafb', minHeight: '100%' }}>
            {/* Header */}
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Collab List</h1>
                    <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 14 }}>Manage your creator collaborations and deals</p>
                </div>
                {error && <button onClick={fetchDeals} style={{ padding: '8px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Retry</button>}
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: 64 }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                    <div style={{ color: '#9ca3af', fontSize: 14 }}>Loading deals...</div>
                </div>
            )}
            {!loading && error && (
                <div style={{ textAlign: 'center', padding: 48, background: '#fee2e2', borderRadius: 16, marginBottom: 20 }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>⚠️</div>
                    <div style={{ fontWeight: 600, color: '#991b1b' }}>{error}</div>
                </div>
            )}

            {/* Stats + Filters + Grid */}
            {!loading && (
                <>
                    {/* Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
                        {[
                            { label: 'Total Deals', value: totalDeals, color: '#111827', extra: null },
                            { label: 'Active', value: activeDeals, color: '#16a34a', extra: null },
                            { label: 'YCP Deals', value: bcDeals, color: '#111827', extra: <span style={{ padding: '2px 6px', background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 3 }}>YCP</span> },
                            { label: 'Pending Consent', value: pendingConsent, color: '#d97706', extra: null },
                        ].map(s => (
                            <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', border: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
                                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{s.label}</div>
                                </div>
                                {s.extra}
                            </div>
                        ))}
                    </div>

                    {/* Filters */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={sel}>
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="completed">Completed</option>
                                <option value="canceled">Canceled</option>
                            </select>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#374151' }}>
                                <input type="checkbox" checked={bcOnlyFilter} onChange={e => setBcOnlyFilter(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#dc2626' }} />
                                YCP Campaigns Only
                                <span style={{ padding: '2px 6px', background: '#dc2626', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 3 }}>YCP</span>
                            </label>
                        </div>
                        <button style={{ padding: '10px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Create Deal</button>
                    </div>

                    {/* Deals Grid */}
                    {filteredDeals.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 64, background: '#fff', borderRadius: 16, border: '1px solid #f3f4f6' }}>
                            <div style={{ fontSize: 48, marginBottom: 12 }}>🤝</div>
                            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>No deals found</div>
                            <p style={{ color: '#9ca3af', fontSize: 14 }}>Try adjusting your filters or create a new deal</p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16 }}>
                            {filteredDeals.map(deal => (
                                <DealCard
                                    key={deal.id}
                                    deal={deal}
                                    onComplete={() => { }}
                                    onCancel={() => { }}
                                    onSendReminder={() => { }}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
